/**
 * The entities a tool returned, inside its row's fold the way Cursor lists a
 * search's files: one line per entity (name, then muted detail). The kind
 * shows only when the list mixes kinds. Clicking a line previews it in the
 * artifact panel when the surface has one, otherwise navigates.
 */
import { ArrowUpRight, PanelRight } from "lucide-react";
import type { FC } from "react";

import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import { useOpenEntity } from "../hooks/useOpenEntity";
import { kindLabel, splitRefTitle } from "../refs/entityRefs";
import type { ToolResultEntity } from "../refs/toolEntities";

type Props = {
  entities: ToolResultEntity[];
  className?: string;
};

export const ToolResultList: FC<Props> = ({ entities, className }) => {
  const { open, previews } = useOpenEntity();
  if (!entities.length) return null;
  const mixed = new Set(entities.map((e) => e.target.kind)).size > 1;
  return (
    <ul
      className={cn(
        "divide-y divide-line-1 overflow-hidden rounded-md bg-fill-1 text-sm",
        className
      )}
      data-testid="tool-result-list"
    >
      {entities.map((e) => {
        const { kind, detail } = splitRefTitle(e.title);
        const OpenIcon = previews(e) ? PanelRight : ArrowUpRight;
        return (
          <li key={e.target.path}>
            <button
              type="button"
              onClick={() => open(e)}
              data-testid="tool-result-row"
              data-kind={e.target.kind}
              className="group/result flex h-6.5 w-full items-center gap-2 px-2 text-left hover:bg-fill-2 focus-visible:bg-fill-2 focus-visible:outline-none"
            >
              <span className="min-w-0 shrink truncate text-fg">{e.label}</span>
              {detail ? (
                <span className="min-w-0 shrink truncate text-fg-3">
                  {detail}
                </span>
              ) : null}
              <span className="flex-1" />
              {mixed ? <Tag>{kind ?? kindLabel(e.target)}</Tag> : null}
              <OpenIcon className="size-3 shrink-0 text-fg-3 opacity-0 group-hover/result:opacity-100 group-focus-visible/result:opacity-100" />
            </button>
          </li>
        );
      })}
    </ul>
  );
};
